/**
 * The fetch wrapper. It prefixes, correlates, guards and parses errors — and nothing else.
 *
 * It does not compute, sort, filter, merge or default anything a payload contains. A response is
 * handed on exactly as the server sent it, because the moment this layer starts deciding, two
 * answers exist for the same question and only one of them has a validator behind it.
 */

import type { components } from './types';
import { readSession } from '~/auth/jwt';

const BASE_PATH = '/api/v1';

export type ErrorBody = components['schemas']['ErrorBody'];

export interface ApiInit extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /**
   * Bytes to send as-is, for an upload. Set this instead of `body`: a corpus is a file, not a
   * JSON document, and stringifying it would send the word "[object ArrayBuffer]".
   */
  raw?: ArrayBuffer;
  /** Sent as `If-Match` on a write to a versioned resource. */
  etag?: string | null;
  signal?: AbortSignal;
}

export interface ApiResult<T> {
  data: T;
  etag: string | null;
  status: number;
}

/** Every failure the API reports, with the correlation id a log search needs. */
export class ApiError extends Error {
  readonly code: ErrorBody['code'];
  readonly problems: readonly string[];
  readonly requestId: string | null;
  readonly status: number;
  /** On a 409, the representation as it now stands. */
  readonly current: Record<string, unknown> | null;

  constructor(status: number, body: ErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.code = body.code;
    this.problems = body.problems ?? [];
    this.requestId = body.requestId ?? null;
    this.status = status;
    this.current = (body.current as Record<string, unknown> | null) ?? null;
  }

  get isConflict(): boolean {
    return this.status === 409;
  }
}

/**
 * The backend guarantees JSON for every API path including its 404s, so an HTML body from an
 * `/api/` path is a bug rather than a case to handle gracefully. Throwing a distinct error makes
 * it visible instead of rendering an error page into a data grid.
 */
export class MalformedResponseError extends Error {
  readonly requestId: string;
  readonly status: number;

  constructor(status: number, requestId: string, detail: string) {
    super(`The API answered with something that is not JSON: ${detail}`);
    this.name = 'MalformedResponseError';
    this.requestId = requestId;
    this.status = status;
  }
}

export class NetworkError extends Error {
  readonly requestId: string;

  constructor(requestId: string, cause: unknown) {
    super('The engine could not be reached.');
    this.name = 'NetworkError';
    this.requestId = requestId;
    this.cause = cause;
  }
}

function newRequestId(): string {
  const random = globalThis.crypto?.randomUUID?.();
  if (random) return random;
  return `req-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

type TokenSource = () => Promise<string | null>;

let accessToken: TokenSource = async () => readSession()?.token ?? null;
/**
 * The default reads the stored session directly rather than returning null.
 *
 * `AuthProvider` installs the real source, but React decides when: on a fresh page load the
 * sidebar's reads fire while `/auth/config` is still in flight, before any provider has been
 * chosen. A default that returns null leaves that first burst unauthenticated — which against an
 * API that does not gate reads looks exactly like working software. The token is in storage and
 * can be read synchronously, so there is no reason to depend on the render order at all.
 */

/**
 * The auth provider registers its token source here at start-up. In v1 it returns null, which is
 * the whole of the seam: the wrapper already attaches a bearer token when one exists.
 */
export function setAccessTokenSource(source: TokenSource): void {
  accessToken = source;
}

export async function apiFetch<T>(path: string, init: ApiInit = {}): Promise<ApiResult<T>> {
  const requestId = newRequestId();
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  headers.set('X-Request-Id', requestId);

  const { body, etag, raw, ...rest } = init;
  if (body !== undefined && raw === undefined) headers.set('Content-Type', 'application/json');
  if (etag) headers.set('If-Match', etag);

  const token = await accessToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  let response: Response;
  try {
    response = await fetch(`${BASE_PATH}${path}`, {
      ...rest,
      headers,
      body: raw ?? (body === undefined ? null : JSON.stringify(body)),
      // A retired register code answers 301 to its canonical path; following it is the point.
      redirect: 'follow',
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new NetworkError(requestId, cause);
  }

  if (response.status === 204) {
    return { data: undefined as T, etag: response.headers.get('ETag'), status: response.status };
  }

  const text = await response.text();
  let payload: unknown;
  try {
    payload = text.length === 0 ? null : JSON.parse(text);
  } catch {
    throw new MalformedResponseError(response.status, requestId, text.slice(0, 120));
  }

  if (!response.ok) {
    const envelope = payload as { error?: ErrorBody } | null;
    if (!envelope?.error) {
      throw new MalformedResponseError(response.status, requestId, text.slice(0, 120));
    }
    throw new ApiError(response.status, { ...envelope.error, requestId: envelope.error.requestId ?? requestId });
  }

  return { data: payload as T, etag: response.headers.get('ETag'), status: response.status };
}

/** The common case: the body, with the ETag discarded. */
export async function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  const result = await apiFetch<T>(path, signal ? { signal } : {});
  return result.data;
}

/** The sentences to render for any failure, in the order they should be read. */
export function problemsOf(error: unknown): readonly string[] {
  if (error instanceof ApiError) {
    return error.problems.length > 0 ? error.problems : [error.message];
  }
  if (error instanceof MalformedResponseError || error instanceof NetworkError) {
    return [error.message];
  }
  if (error instanceof Error) return [error.message];
  return ['Something went wrong.'];
}

export function requestIdOf(error: unknown): string | null {
  if (error instanceof ApiError) return error.requestId;
  if (error instanceof MalformedResponseError || error instanceof NetworkError) {
    return error.requestId;
  }
  return null;
}
